import { GoTrueClient } from '@supabase/auth-js';

export type SupabaseAuthClient = GoTrueClient;
export type SupabaseAuthFacade = {
  auth: SupabaseAuthClient;
};

let sharedSupabaseAuthClient: SupabaseAuthClient | null = null;
const memoryStorage = new Map<string, string>();

export function getBrowserSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getBrowserLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// PKCE code verifiers must live in localStorage, not sessionStorage: recovery
// and magic-link emails open in a fresh tab where per-tab sessionStorage is
// empty, which would make the ?code= exchange impossible. The verifier is a
// one-time random string (useless without the auth code) that auth-js removes
// right after the exchange, so it never persists a session across tabs.
const isCodeVerifierKey = (key: string): boolean => key.endsWith('-code-verifier');

export function buildSessionStorageAdapter() {
  const resolveStorage = (key: string): Storage | null =>
    isCodeVerifierKey(key) ? getBrowserLocalStorage() : getBrowserSessionStorage();

  return {
    getItem(key: string): string | null {
      const storage = resolveStorage(key);
      if (storage) {
        return storage.getItem(key);
      }
      return memoryStorage.get(key) ?? null;
    },
    setItem(key: string, value: string): void {
      const storage = resolveStorage(key);
      if (storage) {
        storage.setItem(key, value);
        return;
      }
      memoryStorage.set(key, value);
    },
    removeItem(key: string): void {
      const storage = resolveStorage(key);
      if (storage) {
        storage.removeItem(key);
        return;
      }
      memoryStorage.delete(key);
    },
  };
}

export function getSupabaseConfig(): { supabaseUrl: string; supabaseAnonKey: string } {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
    );
  }

  return { supabaseUrl, supabaseAnonKey };
}

export function getSupabaseAuthStorageKey(supabaseUrl: string): string {
  const hostname = new URL(supabaseUrl).hostname;
  return `sb-${hostname.split('.')[0]}-auth-token`;
}

export function getSharedSupabaseAuthClient(): SupabaseAuthClient {
  if (!sharedSupabaseAuthClient) {
    const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig();

    sharedSupabaseAuthClient = new GoTrueClient({
      url: new URL('auth/v1', supabaseUrl).href,
      headers: {
        Authorization: `Bearer ${supabaseAnonKey}`,
        apikey: supabaseAnonKey,
      },
      detectSessionInUrl: true,
      flowType: 'pkce',
      persistSession: true,
      storage: buildSessionStorageAdapter(),
      storageKey: getSupabaseAuthStorageKey(supabaseUrl),
    });
  }

  return sharedSupabaseAuthClient;
}

export function getSharedSupabaseAuthFacade(): SupabaseAuthFacade {
  return { auth: getSharedSupabaseAuthClient() };
}

export function resetSharedSupabaseAuthClient(): void {
  sharedSupabaseAuthClient = null;
  memoryStorage.clear();
}
